<template>
    <div>
        <div v-if="connections.length > 1">
            <div>{{ markRelatedDialogText }}</div>
            <pc-select
                v-model="selectedConnectionId"
                v-bind:options="connections"
                v-bind:disabled="lookupLoading"
                v-bind:label="channelSelectLabel"
                text-prop="ChannelName"
                value-prop="ConnectionId"
                class="mb-3" />
        </div>
        <div v-else>
            {{ markRelatedFromSingleConnectionText }}
        </div>
        <div class="d-flex align-items-end">
            <pc-input
                id="LookupRelatedProjectNumberInput"
                ref="lookupProjectNumberInput"
                v-model="lookupProjectNumber"
                v-bind:validator="projectNumberValidator"
                v-bind:disabled="lookupLoading"
                v-bind:label="lookupProjectNumberInputLabel"
                class="me-2 flex-grow-1"
                v-bind:show-validation-errors="false"
                v-on:validated="projectNumberValidationResult = $event" />
            <pc-button v-on:click="onCheckProjectNumber">
                {{ lookupProjectBtnLabel }}
            </pc-button>
        </div>
        <div class="row w-100">
            <validation-error
                v-bind:validation-error="validationError"
                class="mt-0" />
        </div>
        <div
            v-if="projectLookupResult != null"
            class="mt-3">
            <div
                v-if="showProjectNotFound"
                class="text-danger">
                {{ projectNotFoundText }}
            </div>
            <div v-else>
                <div class="text-success">
                    {{ foundProjectText }}
                </div>
                <template
                    v-for="(dataPair, i) in projectLookupResult.ProjectData"
                    v-bind:key="`${dataPair.SlotName}-${i}`">
                    <div>
                        <span>{{ dataPair.SlotName }}</span>
                        <span>:</span>
                        <span v-if="dataPair.SlotValue">
                            {{ dataPair.SlotValue }}
                        </span>
                        <span
                            v-else
                            class="text-body-secondary fst-italic">
                            {{ emptySlotValuePlaceholder }}
                        </span>
                    </div>
                </template>
            </div>
            <div v-if="Boolean(lookupSearchResults)">
                <div>
                    {{ additionalLookupInfoHeader }}
                </div>
                <ul>
                    <li
                        v-for="searchResult in lookupSearchResults"
                        v-bind:key="searchResult">
                        {{ searchResult }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
<script>
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import validationError from "Validation/_validation-error-helper.vue";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/markProjectRelatedDialog.i18n";

    export const GET_RESULT = "getResult";
    export const IS_VALID = "isValid";
    export const CANCEL_RESULT = "CANCEL";
    export const OK_RESULT = "OK";

    export default {
        components: {
            validationError
        },
        props: {
            lookupProjectAsyncFn: { type: Function, required: true },
            connections: { type: Array, required: true }
        },
        data() {
            return {
                selectedConnectionId: this.connections[0].ConnectionId,

                lookupProjectNumber: null,
                projectLookupResult: null,
                projectNumberValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                projectNumberValidationResult: null,
                needsLookupValidationError: null,
                lookupLoading: false
            };
        },
        computed: {
            validationError() {
                return (
                    (this.projectNumberValidationResult && this.projectNumberValidationResult.error) ||
                    this.needsLookupValidationError
                );
            },
            hasValidationError() {
                return this.projectNumberValidationResult && this.projectNumberValidationResult.hasError;
            },
            showProjectNotFound() {
                return this.projectLookupResult != null && !this.lookedUpProjectId;
            },
            lookedUpProjectId() {
                return this.projectLookupResult && this.projectLookupResult.ProjectId;
            },
            lookupSearchResults() {
                return this.projectLookupResult && this.projectLookupResult.SearchResults;
            },
            selectedConnectionName() {
                if (!this.selectedConnectionId) {
                    return "";
                }
                const selectedConnection = this.connections.find((c) => {
                    return c.ConnectionId === this.selectedConnectionId;
                });
                return selectedConnection.ChannelName;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    foundProjectText() {
                        return { key: KEYS.foundProjectText, values: { projectNumber: this.lookupProjectNumber } };
                    },
                    markRelatedFromSingleConnectionText() {
                        return {
                            key: KEYS.markRelatedFromSingleConnectionText,
                            values: { channelName: this.selectedConnectionName }
                        };
                    }
                },
                NAMESPACE
            )
        },
        watch: {
            // If either of these are changed after lookup, need to clear the lookup result and do it again
            lookupProjectNumber() {
                this.clearResults();
            },
            selectedConnectionId() {
                this.clearResults();
            }
        },
        methods: {
            clearResults() {
                this.projectLookupResult = null;
                this.needsLookupValidationError = null;
            },
            async onCheckProjectNumber() {
                this.$refs.lookupProjectNumberInput.validate();

                if (this.hasValidationError) {
                    return;
                }

                if (this.lookupLoading) {
                    return;
                }

                this.clearResults();

                try {
                    this.lookupLoading = true;
                    const result = await this.lookupProjectAsyncFn({
                        projectNumber: this.lookupProjectNumber,
                        connectionId: this.selectedConnectionId
                    });
                    this.projectLookupResult = result.Data;
                } finally {
                    this.lookupLoading = false;
                }
            },
            [IS_VALID](result) {
                if (result === CANCEL_RESULT) {
                    return true;
                }

                if (!this.lookedUpProjectId) {
                    this.needsLookupValidationError = this.needsLookupValidationMessage;
                } else {
                    this.needsLookupValidationError = null;
                }

                return Boolean(this.lookedUpProjectId) && Boolean(this.selectedConnectionId);
            },
            [GET_RESULT]() {
                return {
                    projectId: this.lookedUpProjectId,
                    connectionId: this.selectedConnectionId
                };
            }
        }
    };
</script>
